import escapeHTML from './escapeHTML';
import parseHTMLBody, {type ParsedNode} from './parseHTMLBody';

const DEFAULT_ALLOWED_TAGS: Array<string> = [
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'CODE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'NL',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRIKE',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'UL',
];

const DEFAULT_ALLOWED_ATTRIBUTES: Record<string, Array<string>> = {
  A: ['href', 'title', 'target'],
  IMG: ['alt', 'src', 'title'],
};

const ALLOWED_SCHEMES: Array<string> = ['http://', 'https://', 'mailto:'];

// Characters a browser discards from the ends of a URL before resolving it:
// the C0 controls (U+0000-U+001F) and the space (U+0020).
const isURLPadding = (charCode: number): boolean => charCode <= 0x20;

/**
 * Strips the characters a browser ignores when working out what scheme a URL
 * has: leading and trailing C0 controls and spaces, and tabs and newlines from
 * anywhere inside the value.
 *
 * The allowlist is checked against the normalized URL and the normalized URL is
 * what gets emitted, so there's no gap between what was approved and what the
 * browser will navigate to. Padding like `href="  https://example.com"` is a
 * real link to a browser, and used to be discarded here for looking like one
 * with no scheme.
 */
function normalizeURL(url: string): string {
  // Tabs and newlines are ignored anywhere inside a URL, not only at the ends.
  const stripped = url.replace(/[\t\n\r]/g, '');
  let start = 0;
  let end = stripped.length;
  while (start < end && isURLPadding(stripped.charCodeAt(start))) {
    start++;
  }
  while (end > start && isURLPadding(stripped.charCodeAt(end - 1))) {
    end--;
  }
  return stripped.slice(start, end);
}

const NON_PRESERVING_TAGS = ['A', 'SPAN'];
const SELF_CLOSING = ['IMG', 'BR', 'HR'];

export default function sanitize(
  markup: string,
  {
    allowedTags = DEFAULT_ALLOWED_TAGS,
    allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES,
  }: {
    allowedTags?: typeof DEFAULT_ALLOWED_TAGS;
    allowedAttributes?: typeof DEFAULT_ALLOWED_ATTRIBUTES;
  } = {},
): string {
  if (!markup) {
    return '';
  }

  const output: Array<string> = [];

  function walk(nodes: Array<ParsedNode>) {
    for (const node of nodes) {
      if (node.type === 'text') {
        output.push(escapeHTML(node.value));
        continue;
      }

      const {tagName} = node;
      if (!allowedTags.includes(tagName)) {
        // A disallowed tag takes its subtree with it, so that markup like
        // `<script>alert(1)</script>` doesn't leave its body behind as text.
        continue;
      }

      const elemAllowedAttributes = allowedAttributes[tagName] || [];
      const hasAllowedAttributes = node.attributes.some(attr =>
        elemAllowedAttributes.includes(attr.name),
      );
      const shouldPreserveTag =
        !NON_PRESERVING_TAGS.includes(tagName) || hasAllowedAttributes;
      const lowerName = tagName.toLowerCase();

      if (shouldPreserveTag) {
        output.push(`<${lowerName}`);

        if (hasAllowedAttributes) {
          for (const attr of node.attributes) {
            if (!elemAllowedAttributes.includes(attr.name)) {
              continue;
            }
            let {value} = attr;
            if (attr.name === 'src' || attr.name === 'href') {
              value = normalizeURL(value);
              if (
                !ALLOWED_SCHEMES.some(scheme =>
                  value.toLowerCase().startsWith(scheme),
                )
              ) {
                continue;
              }
            }
            output.push(` ${escapeHTML(attr.name)}="${escapeHTML(value)}"`);
          }
        }

        output.push('>');

        if (SELF_CLOSING.includes(tagName)) {
          // Void elements never have children and take no closing tag.
          continue;
        }
      }

      walk(node.children);

      if (shouldPreserveTag) {
        output.push(`</${lowerName}>`);
      }
    }
  }

  walk(parseHTMLBody(markup));

  return output.join('');
}
